import { MouseEvent, useEffect, useRef, useState } from 'react'
import useMediaQuery from 'hooks/use-media-query'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'

interface RecapVideoProps {
  mobileSrc: string
  desktopSrc: string
  // Viewport width from which desktopSrc plays as a silent autoplay loop
  desktopMinWidth?: number
  className?: string
  trackSection: string
}

// Delay before the play control fades out while the clip is running
const CONTROLS_HIDE_DELAY = 2000

const PlayIcon = () => (
  <svg width="28" height="32" viewBox="0 0 28 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M2 2.5v27a1 1 0 0 0 1.52.86l23-13.5a1 1 0 0 0 0-1.72l-23-13.5A1 1 0 0 0 2 2.5Z" fill="currentColor" />
  </svg>
)

const PauseIcon = () => (
  <svg width="24" height="28" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="2" y="2" width="7" height="24" rx="1" fill="currentColor" />
    <rect x="15" y="2" width="7" height="24" rx="1" fill="currentColor" />
  </svg>
)

const SoundIcon = ({ muted }: { muted: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M4 9v6h4l5 4V5L8 9H4Z" fill="currentColor" />
    {muted ? (
      <path d="m16 9 5 6m0-6-5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    ) : (
      <>
        <path d="M16 8.5a5 5 0 0 1 0 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M18.5 6a9 9 0 0 1 0 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    )}
  </svg>
)

/**
 * Recap clip with art direction per breakpoint. Desktop/tablet plays a
 * silent autoplay loop like `VideoBackground`; mobile never autoplays and
 * exposes play/pause and sound controls instead (iOS shows the first frame
 * thanks to preload="metadata"). Crossing the breakpoint remounts the
 * <video> through `key={src}` so the new source loads from scratch.
 */
const RecapVideo = ({ mobileSrc, desktopSrc, desktopMinWidth = 768, className, trackSection }: RecapVideoProps) => {
  const isDesktop = useMediaQuery(`(min-width: ${desktopMinWidth}px)`)
  const videoRef = useRef<HTMLVideoElement>(null)
  const hideTimer = useRef<number>()
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(true)
  const [controlsVisible, setControlsVisible] = useState(true)

  // Asset filenames may contain spaces: always encode
  const src = encodeURI(isDesktop ? desktopSrc : mobileSrc)

  const track = (element: string) =>
    dlTrackEvent('user_interaction', { action: 'click', element: `recap :: ${element}`, section: trackSection })

  // Fresh interaction state whenever the source (and so the element) changes
  useEffect(() => {
    setPlaying(false)
    setMuted(true)
    setControlsVisible(true)
  }, [src])

  // React 17 does not render the muted attribute: mirror the state by hand.
  // On desktop this also satisfies the autoplay policy before play().
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    video.muted = isDesktop || muted
    if (video.muted) video.setAttribute('muted', '')
    else video.removeAttribute('muted')
    if (isDesktop) video.play().catch(() => undefined)
  }, [src, isDesktop, muted])

  // Mobile: the play control fades out after a short idle while playing
  useEffect(() => {
    window.clearTimeout(hideTimer.current)
    if (isDesktop || !playing || !controlsVisible) return undefined
    hideTimer.current = window.setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_DELAY)
    return () => window.clearTimeout(hideTimer.current)
  }, [isDesktop, playing, controlsVisible])

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return
    setControlsVisible(true)
    if (video.paused) {
      track('play')
      video.play().catch(() => undefined)
    } else {
      track('pause')
      video.pause()
    }
  }

  const toggleSound = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setControlsVisible(true)
    track(muted ? 'sound on' : 'sound off')
    setMuted(!muted)
  }

  const handlePlayButton = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    togglePlay()
  }

  if (isDesktop) {
    return (
      <div className={`recap-video ${className ?? ''}`}>
        <video key={src} ref={videoRef} className="recap-video__media" src={src} autoPlay muted loop playsInline />
      </div>
    )
  }

  const playClass = [
    'recap-video__play',
    playing ? 'recap-video__play--playing' : '',
    playing && !controlsVisible ? 'recap-video__play--hidden' : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={`recap-video recap-video--mobile ${className ?? ''}`} onClick={togglePlay}>
      <video
        key={src}
        ref={videoRef}
        className="recap-video__media"
        src={src}
        loop
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <button
        type="button"
        className={playClass}
        aria-label={playing ? 'Pausar' : 'Reproducir'}
        onClick={handlePlayButton}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>
      <button
        type="button"
        className="recap-video__sound"
        aria-label={muted ? 'Activar sonido' : 'Silenciar'}
        aria-pressed={!muted}
        onClick={toggleSound}
      >
        <SoundIcon muted={muted} />
      </button>
    </div>
  )
}

export default RecapVideo
